import React, { createContext, useEffect, useReducer } from 'react'
import { CandidateMeSummary, candidateAgencyInfo, candidateGetMe } from '../api/candidateApi'

export interface CandidateState {
  candidate: CandidateMeSummary | null
  isAuthenticated: boolean
  isAuthLoading: boolean
  // Pending agencyid captured from query string at landing — used during
  // register/login. Persisted to sessionStorage so refresh inside the flow
  // doesn't lose it.
  pendingAgencyId: number | null
  // Fetched from the public agency-info endpoint whenever pendingAgencyId
  // changes, so every anonymous page can refer to the agency by name
  // without duplicating the lookup.
  pendingAgencyName: string | null
}

export type CandidateAction =
  | { type: 'SET_CANDIDATE'; payload: CandidateMeSummary }
  | { type: 'LOGOUT' }
  | { type: 'SET_AUTH_LOADING'; payload: boolean }
  | { type: 'SET_PENDING_AGENCY'; payload: number | null }
  | { type: 'SET_PENDING_AGENCY_NAME'; payload: string | null }

const PENDING_AGENCY_KEY = 'cdreams_candidate_pending_agency'

const initialState: CandidateState = {
  candidate: null,
  isAuthenticated: false,
  isAuthLoading: true,
  pendingAgencyId: null,
  pendingAgencyName: null,
}

function reducer(state: CandidateState, action: CandidateAction): CandidateState {
  switch (action.type) {
    case 'SET_CANDIDATE':
      return {
        ...state,
        candidate: action.payload,
        isAuthenticated: true,
        isAuthLoading: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        candidate: null,
        isAuthenticated: false,
        isAuthLoading: false,
      }
    case 'SET_AUTH_LOADING':
      return { ...state, isAuthLoading: action.payload }
    case 'SET_PENDING_AGENCY':
      if (action.payload == null) {
        sessionStorage.removeItem(PENDING_AGENCY_KEY)
      } else {
        sessionStorage.setItem(PENDING_AGENCY_KEY, String(action.payload))
      }
      // Clear the cached name whenever the id changes; the effect below
      // will refetch it from the backend.
      return { ...state, pendingAgencyId: action.payload, pendingAgencyName: null }
    case 'SET_PENDING_AGENCY_NAME':
      return { ...state, pendingAgencyName: action.payload }
    default:
      return state
  }
}

export const CandidateStateContext = createContext<{
  state: CandidateState
  dispatch: React.Dispatch<CandidateAction>
} | null>(null)

function loadPendingAgencyFromStorage(): number | null {
  try {
    const raw = sessionStorage.getItem(PENDING_AGENCY_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export const CandidateStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    pendingAgencyId: loadPendingAgencyFromStorage(),
  })

  // On mount, check whether the candidate already has a valid session cookie.
  // Hydrating from the server is what makes the portal survive page refresh.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const res = await candidateGetMe()
      if (cancelled) return
      if ('error' in res) {
        dispatch({ type: 'LOGOUT' })
      } else {
        dispatch({ type: 'SET_CANDIDATE', payload: res.candidate })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Auto-resolve the pending agency name whenever the pending agency id
  // changes. One fetch, shared by every anonymous page that references
  // the agency (landing, register, login, forgot-password).
  useEffect(() => {
    if (!state.pendingAgencyId) return
    let cancelled = false
    ;(async () => {
      const res = await candidateAgencyInfo(state.pendingAgencyId as number)
      if (cancelled) return
      if (!('error' in res)) {
        dispatch({ type: 'SET_PENDING_AGENCY_NAME', payload: res.name })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [state.pendingAgencyId])

  return (
    <CandidateStateContext.Provider value={{ state, dispatch }}>
      {children}
    </CandidateStateContext.Provider>
  )
}
